import { IDateRangeKey } from '@query'
import cn from 'classnames'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { FaCalendarDay } from 'react-icons/fa'
import styled from 'styled-components'

/** Presets grouped for the menu. Unlisted keys still appear under Everything. */
export const PRESET_GROUPS: { titleKey: string; keys: IDateRangeKey[]; hasCustom?: boolean }[] = [
  { titleKey: 'accountingPeriod', keys: ['period-this', 'period-prev'] },
  { titleKey: 'monthAndYear', keys: ['month-this', 'month-prev', 'year-this', 'year-prev'] },
  { titleKey: 'everything', keys: ['all-history'], hasCustom: true }
]

export interface DateRangePresetGroup {
  titleKey: string
  keys: IDateRangeKey[]
  hasCustom?: boolean
}

interface Props {
  groups: DateRangePresetGroup[]
  selectedKey?: IDateRangeKey
  isCustom: boolean
  onPreset: (key: IDateRangeKey) => void
  onCustom: () => void
}

export const DateRangePresets: React.FC<Props> = ({
  groups,
  selectedKey,
  isCustom,
  onPreset,
  onCustom
}) => {
  const [t] = useTranslation()

  return (
    <>
      {groups.map(group => (
        <Group key={group.titleKey}>
          <GroupTitle>{t(`components.dateRangeFilter.group.${group.titleKey}`)}</GroupTitle>
          <Options>
            {group.keys.map(key => (
              <Option
                key={key}
                type="button"
                className={cn({ isSelected: selectedKey === key })}
                data-test="date-range-option"
                data-value={key}
                onClick={() => onPreset(key)}
              >
                {t(`components.dateRangeFilter.${key}`)}
              </Option>
            ))}
            {group.hasCustom && (
              <Option
                type="button"
                className={cn({ isSelected: isCustom })}
                data-test="date-range-option"
                data-value="custom"
                onClick={onCustom}
              >
                <FaCalendarDay />
                {t('components.dateRangeFilter.custom')}
              </Option>
            )}
          </Options>
        </Group>
      ))}
    </>
  )
}

const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs}rem;
`

const GroupTitle = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xxs}rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.iconGray};
`

const Options = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.xxs}rem;
`

const Option = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  padding: ${({ theme }) => theme.spacing.xs}rem ${({ theme }) => theme.spacing.sm}rem;
  border: none;
  border-radius: 0.5625rem;
  background: ${({ theme }) => theme.colors.neutralGray};
  color: ${({ theme }) => theme.colors.metalGray};
  font-family: inherit;
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  font-weight: 500;
  white-space: nowrap;
  cursor: pointer;

  svg {
    font-size: ${({ theme }) => theme.fontSize.px10}rem;
  }

  &:hover {
    background: ${({ theme }) => theme.dashboard.infoTint};
  }

  &.isSelected {
    background: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.neutralWhite};
  }
`
